import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { adminService, type KPAFrameworkItem, type KPACreate, type KPAUpdate } from '@/services/api/adminService';
import toast from 'react-hot-toast';

interface KPAFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  kpa?: KPAFrameworkItem | null;
  mode: 'create' | 'edit';
  strategicPillars: Array<{ id: string; name: string }>;
}

const KPAFormModal: React.FC<KPAFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  kpa,
  mode,
  strategicPillars
}) => {
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    strategic_pillar_id: '',
    weight_percentage: '',
    sort_order: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && kpa) {
      setFormData({
        name: kpa.name || '',
        display_name: kpa.display_name || '',
        description: kpa.description || '',
        strategic_pillar_id: '', // We'll need to get this from the KPA
        weight_percentage: kpa.weight_percentage?.toString() || '',
        sort_order: kpa.sort_order?.toString() || ''
      });
    } else {
      setFormData({
        name: '',
        display_name: '',
        description: '',
        strategic_pillar_id: strategicPillars[0]?.id || '',
        weight_percentage: '',
        sort_order: ''
      });
    }
  }, [mode, kpa, strategicPillars]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.display_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'create') {
        const createData: KPACreate = {
          name: formData.name,
          display_name: formData.display_name || null,
          description: formData.description || null,
          strategic_pillar_id: formData.strategic_pillar_id,
          weight_percentage: formData.weight_percentage ? parseFloat(formData.weight_percentage) : null,
          sort_order: formData.sort_order ? parseInt(formData.sort_order) : null
        };
        
        await adminService.createKPA(createData);
        toast.success('KPA created successfully');
      } else if (kpa) {
        const updateData: KPAUpdate = {
          name: formData.name,
          display_name: formData.display_name || null,
          description: formData.description || null,
          weight_percentage: formData.weight_percentage ? parseFloat(formData.weight_percentage) : null,
          sort_order: formData.sort_order ? parseInt(formData.sort_order) : null
        };
        
        await adminService.updateKPA(kpa.id, updateData);
        toast.success('KPA updated successfully');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving KPA:', error);
      toast.error(`Failed to ${mode} KPA`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'create' ? 'Create New KPA' : 'Edit KPA'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-sm font-medium text-gray-700">
              Name (Unique Identifier) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., operating-financial"
              className="mt-1"
              disabled={mode === 'edit'} // Don't allow changing the name on edit
            />
            <p className="text-xs text-gray-500 mt-1">
              Lowercase with hyphens (e.g., culture-people)
            </p>
          </div>

          {/* Display Name */}
          <div>
            <Label htmlFor="display_name" className="text-sm font-medium text-gray-700">
              Display Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="display_name"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              placeholder="e.g., Operating and Financial Results"
              className="mt-1"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description" className="text-sm font-medium text-gray-700">
              Description
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this KPA..."
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Strategic Pillar (only for create) */}
          {mode === 'create' && (
            <div>
              <Label htmlFor="strategic_pillar_id" className="text-sm font-medium text-gray-700">
                Primary Strategic Pillar <span className="text-red-500">*</span>
              </Label>
              <select
                id="strategic_pillar_id"
                value={formData.strategic_pillar_id}
                onChange={(e) => setFormData({ ...formData, strategic_pillar_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-heritage-blue"
              >
                {strategicPillars.map((pillar) => (
                  <option key={pillar.id} value={pillar.id}>
                    {pillar.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Note: KPA can link to multiple pillars through Individual Objectives
              </p>
            </div>
          )}

          {/* Weight Percentage */}
          <div>
            <Label htmlFor="weight_percentage" className="text-sm font-medium text-gray-700">
              Weight Percentage (%)
            </Label>
            <Input
              id="weight_percentage"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={formData.weight_percentage}
              onChange={(e) => setFormData({ ...formData, weight_percentage: e.target.value })}
              placeholder="e.g., 25"
              className="mt-1"
            />
          </div>

          {/* Sort Order */}
          <div>
            <Label htmlFor="sort_order" className="text-sm font-medium text-gray-700">
              Sort Order
            </Label>
            <Input
              id="sort_order"
              type="number"
              min="0"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
              placeholder="e.g., 1"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Lower numbers appear first
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-heritage-blue hover:bg-heritage-blue/90"
              disabled={loading}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create KPA' : 'Update KPA'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KPAFormModal;
